import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent, Card, CardService, ScoreExplanation } from 'lib-trend-core';
import { takeUntil } from 'rxjs';

/**
 * Explains how a deal's current score was reached (RF-36, RF-41, AC-20).
 *
 * Reads the STORED breakdown through the API rather than recomputing, so the
 * explanation always matches the number shown on the card (plan AD-08). Criteria
 * whose block was not applicable are listed as such — never as a zero score, which
 * would read as "failed this criterion" instead of "this did not apply".
 */
@Component({
  selector: 'app-score-explanation',
  templateUrl: './score-explanation.component.html',
  styleUrls: ['./score-explanation.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ScoreExplanationComponent extends AbstractComponent implements OnInit {

  explanation?: ScoreExplanation;

  constructor(
    injector: Injector,
    private readonly cardService: CardService,
    private readonly dialogRef: MatDialogRef<ScoreExplanationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { card: Card },
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.loading = true;

    this.cardService
      .getScoreExplanation(this.data?.card?._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (explanation) => {
          this.explanation = explanation;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.alertService.error(this.t('kanban.scoreExplanation.loadError'));
        },
      });
  }

  get applicableCriteria() {
    return (this.explanation?.criteria ?? []).filter(criterion => criterion.applicable);
  }

  get notApplicableCriteria() {
    return (this.explanation?.criteria ?? []).filter(criterion => !criterion.applicable);
  }

  /** Share of the criterion's own weight that the deal earned, for the progress bar. */
  earnedPercent(criterion: { weight: number; earned: number }): number {
    if (!criterion?.weight) return 0;
    return Math.round((criterion.earned / criterion.weight) * 100);
  }

  /** i18n key for a criterion. Keys are stable ids like `profileFit.email`. */
  criterionLabel(criterion: string): string {
    return `kanban.scoreCriterion.${criterion}`;
  }

  blockLabel(block: string | null): string {
    return block ? `kanban.scoreBlock.${block}` : 'kanban.scoreBlock.OTHER';
  }

  close(): void {
    this.dialogRef.close();
  }
}
